/* eslint-disable react/prop-types */
import React from 'react';
import { Card, Typography } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import EmptyState from './EmptyState';
import styles from './SectionCard.less';

const { Text } = Typography;

/**
 * SectionCard —— 分区卡片（本模块所有内容区块的统一容器）
 * ------------------------------------------------------------------
 * 对应商家端看板的「区块标题 + 灰色说明 + 右上操作」结构，用于替换后台
 * 现有的「蓝色左竖条标题 + Descriptions bordered」堆叠。
 *
 * 基于 antd Card 实现：title 接受 ReactNode，所以把主标题与说明组成一个
 * 节点传入，右侧操作走 antd 原生 extra，间距与分隔线沿用本模块规范。
 *
 * @param title     区块主标题
 * @param subtitle  标题右侧的浅灰说明（截图里每块都有一行，例如
 *                  「近30天 · 截止昨日 · 到店 vs 仅线上打卡」）
 * @param tips      标题下方的一行辅助解释
 * @param extra     右上角操作区（放 FilterDropdown / Button / 链接）
 * @param bordered  是否带描边与底色，默认 true
 * @param bodyStyle 覆盖内容区内边距
 * @param error     错误文案（或节点）：卡片内出错误条 + 重试，不留白屏
 * @param onRetry   重试回调（配合 error 使用）
 * @param empty     无数据：卡片内出空态（一句原因 + 一个动作）
 * @param emptyHint      空态补充说明
 * @param emptyAction    空态动作节点
 */
const SectionCard = (props) => {
  const {
    title,
    subtitle,
    tips,
    extra,
    children,
    loading,
    bordered = true,
    className,
    bodyStyle,
    id,
    error,
    onRetry,
    empty,
    emptyHint,
    emptyAction,
  } = props;

  /* 四态优先级：错误 > 空 > 加载 > 数据。
     错误与空态都渲染在卡片内部（保留标题与操作区），不整块遮罩。 */
  let body = children;
  if (error) {
    body = (
      <div className={styles.stateBox}>
        <ExclamationCircleOutlined className={styles.stateIcon} />
        <span className={styles.stateText}>
          {typeof error === 'string' ? `读取失败：${error}` : error}
        </span>
        {onRetry && (
          <button type="button" className={styles.retry} onClick={onRetry}>
            重试
          </button>
        )}
      </div>
    );
  } else if (empty && !loading) {
    body = (
      <EmptyState
        compact
        description={typeof empty === 'string' ? empty : '暂无数据'}
        hint={emptyHint}
        action={emptyAction}
      />
    );
  }

  const titleNode = (
    <div className={styles.header} id={id}>
      <span className={styles.title}>{title}</span>
      {subtitle && (
        <Text className={styles.subtitle} type="secondary">
          {subtitle}
        </Text>
      )}
    </div>
  );

  return (
    <Card
      className={`${styles.sectionCard} ${bordered ? '' : styles.flat} ${
        className || ''
      }`}
      title={titleNode}
      extra={extra}
      bordered={bordered}
      loading={loading}
      headStyle={{ padding: '10px 16px', minHeight: 44 }}
      bodyStyle={{ padding: '12px 16px 16px', ...bodyStyle }}
    >
      {tips && (
        <div className={styles.tips}>
          <Text type="secondary">{tips}</Text>
        </div>
      )}
      {body}
    </Card>
  );
};

export default SectionCard;
